import React from 'react';
import Navbar from '../navbar/navbar.jsx';
import Footer from '../footer/footer.jsx';
import AnimatedSection from '../components/AnimatedSection.jsx';
import { translations } from './translations';

const ExpLaboral = ({ language, setLanguage, isDarkMode, setIsDarkMode }) => {
  const t = translations[language];

  // Function to parse markdown-style bold text
  const parseMarkdownBold = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <span key={index} className="font-bold">
            {part.slice(2, -2)}
          </span>
        );
      }
      return part;
    });
  };  return (
    <div 
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-stone-200 text-gray-900'
      }`}
      style={{ fontFamily: "Raleway, sans-serif" }}    >
      <link
        href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Raleway:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />
      
      <Navbar
        language={language} 
        setLanguage={setLanguage}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
      />
        <main className="pt-20 pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header Section */}            <AnimatedSection animationType="fadeInUp" className="text-center mb-8 mt-16">
              <h1 className={`text-6xl md:text-7xl font-bold mb-2 font-space-grotesk ${
                isDarkMode ? 'text-red-400' : 'text-red-900'
              }`}>
                {t.title}
              </h1>
              <p className={`text-lg max-w-3xl mb-4 mx-auto ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {t.description}
              </p>              <a 
                href="/docs/InesVaz-CV.pdf" 
                download
                className={`inline-flex items-center px-6 py-3 rounded-lg shadow-md font-semibold transition-all duration-300 transform hover:translate-y-[-2px] ${
                  isDarkMode 
                    ? 'bg-red-600 text-white hover:bg-red-500 hover:shadow-lg' 
                    : 'bg-red-900 text-white hover:bg-red-800 hover:shadow-lg'
                }`}
              >
                <svg 
                  className="w-5 h-5 mr-2" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {t.downloadCV || "Descargar CV"}
              </a>
              <p className={`text-sm mt-3 italic ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t.cvDisclaimer}
              </p>
            </AnimatedSection>

            {/* Experience Section */}
            <div className="space-y-12">
              {t.experiences.map((experience, index) => (
                <AnimatedSection 
                  key={experience.id}
                  animationType="fadeInUp"
                  delay={index * 300}
                >
                  <div className={`
                    ${isDarkMode ? 'bg-gray-700' : 'bg-white'} 
                    rounded-2xl p-8 md:p-12 shadow-lg hover:shadow-xl transition-all duration-300
                  `}>
                    <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                      {/* Text Content - Left Half */}
                      <div className="space-y-6">
                        <div>                      <h2 className={`text-2xl md:text-3xl font-bold mb-2 font-space-grotesk ${
                            isDarkMode ? 'text-white' : 'text-gray-800'
                          }`}>
                            {experience.title}
                          </h2>                      <div className={`text-lg font-semibold mb-1 ${
                            isDarkMode ? 'text-red-400' : 'text-red-700'
                          }`}>
                            {experience.company}
                          </div>
                          <div className={`text-sm font-medium ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {experience.period}
                          </div>
                        </div>
                        
                        <div className={`text-lg leading-relaxed ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {parseMarkdownBold(experience.description)}
                        </div>
                      </div>                      {/* Image - Right Half */}
                      <div className="flex justify-center">
                        <div className={`
                          ${experience.company === "Perceptiva" ? "w-60 h-60 md:w-72 md:h-72" : "w-80 h-80 md:w-96 md:h-96"} overflow-hidden
                          hover:scale-105 transition-transform duration-300
                        `}>
                          <img
                            src={
                              experience.company === "Perceptiva" 
                                ? (isDarkMode ? "/img/perceptiva-logo-white-final.png" : "/img/perceptiva-logo-black.png")
                                : (isDarkMode ? "/img/yowecodeDark.png" : experience.image)
                            }
                            alt={`${experience.title} at ${experience.company}`}
                            className={`w-full h-full ${
                              experience.company === "Perceptiva" ? "object-contain" : "object-cover"
                            }`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          {/* Fallback for missing image */}
                          <div className={`
                            w-full h-full hidden items-center justify-center
                            ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-500'}
                          `}>
                            <div className="text-center">
                              <div className="text-4xl mb-2">💼</div>
                              <div className="font-medium">{experience.company}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </main>
      
      <Footer language={language} isDarkMode={isDarkMode} />
    </div>
  );
};

export default ExpLaboral;